import { useCallback, useEffect, useRef, useState } from 'react';
import { SlidersHorizontal, Plane, Check, X, LayoutDashboard, UserCheck, CalendarDays, Clock, GitBranch, Wallet, FolderKey } from 'lucide-react';
import { apiSettings, apiClients } from '../api/client.js';

/**
 * Sprint 11 — Paramètres applicatifs (feature flags admin).
 *
 * Pour l'instant un seul paramètre : `selfTimeOffRequestEnabled`. Le désactiver
 * coupe la dépose/édition de congés en self-service côté USER (l'onglet
 * "Mes congés" passe en lecture seule). Cas d'usage : les congés validés sont
 * poussés par une application tierce via l'API publique M2M.
 */

const cardStyle = {
  background: 'var(--card-bg)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-md)',
  padding: '20px 24px',
  boxShadow: 'var(--shadow-card)',
};

function ToggleRow({ Icon, title, description, enabled, busy, onToggle }) {
  // Pill toggle conforme standards UI (pas de <input type=checkbox>).
  const pillStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    borderRadius: 'var(--radius-pill)',
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 500,
    cursor: busy ? 'wait' : 'pointer',
    border: '1px solid',
    borderColor: enabled ? 'var(--success)' : 'var(--glass-border)',
    background: enabled ? 'var(--success)' : 'transparent',
    color: enabled ? '#fff' : 'var(--text-muted)',
    opacity: busy ? 0.6 : 1,
    whiteSpace: 'nowrap',
  };
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 24,
      }}
    >
      <div style={{ display: 'flex', gap: 12 }}>
        <Icon size={20} style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 2 }} />
        <div>
          <div style={{ color: 'var(--text)', fontWeight: 600, fontSize: 15 }}>{title}</div>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: '6px 0 0', maxWidth: 560 }}>
            {description}
          </p>
        </div>
      </div>
      <button
        type="button"
        style={pillStyle}
        disabled={busy}
        onClick={() => onToggle(!enabled)}
        title={enabled ? 'Cliquer pour désactiver' : 'Cliquer pour activer'}
      >
        {enabled ? <Check size={14} /> : <X size={14} />}
        {enabled ? 'Activé' : 'Désactivé'}
      </button>
    </div>
  );
}

/**
 * Sprint 13 — ligne de réglage numérique (paramètres du tableau de bord).
 * Commit au blur / Enter ; revert à Échap. Clamp côté UI ([min, max]).
 */
/**
 * Ticket #54 — Client de rattachement imposé aux affaires internes créées par
 * les managers. Sans lui, la création d'affaire interne est refusée (409).
 */
function InternalClientRow({ value, busy, onCommit }) {
  const [clients, setClients] = useState([]);
  const [loadError, setLoadError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    apiClients
      .list('active')
      .then((res) => {
        if (!cancelled) setClients(res.items ?? []);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
      <FolderKey size={18} style={{ color: 'var(--accent)', marginTop: 2 }} />
      <div style={{ flex: 1, minWidth: 220 }}>
        <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: 14 }}>
          Client de rattachement des affaires internes
        </div>
        <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>
          Client auquel sont rattachées les affaires internes créées par les managers (temps non
          facturable, jamais exporté vers Gescom). Tant qu’il n’est pas choisi, les managers ne
          peuvent pas en créer.
        </div>
        {loadError ? (
          <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}>{loadError}</div>
        ) : null}
      </div>
      <select
        aria-label="Client de rattachement des affaires internes"
        value={value ?? ''}
        disabled={busy}
        onChange={(e) => onCommit(e.target.value || null)}
        style={{
          minWidth: 220,
          padding: '8px 10px',
          background: 'var(--glass)',
          color: 'var(--text)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-sm)',
          fontSize: 14,
        }}
      >
        <option value="">— Non configuré —</option>
        {clients.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
    </div>
  );
}

function NumberRow({ Icon, title, description, value, min, max, suffix, busy, onCommit, step = 1, integer = true }) {
  const [draft, setDraft] = useState(String(value ?? ''));
  const lastValue = useRef(value);

  // Resync si la valeur serveur change (chargement / réponse update).
  if (lastValue.current !== value) {
    lastValue.current = value;
    setDraft(String(value ?? ''));
  }

  const commit = () => {
    const raw = Number(String(draft).replace(',', '.'));
    if (!Number.isFinite(raw) || String(draft).trim() === '') {
      setDraft(String(value)); // invalide → revert
      return;
    }
    const n = integer ? Math.round(raw) : raw;
    const clamped = Math.min(max, Math.max(min, n));
    setDraft(String(clamped));
    if (clamped !== value) onCommit(clamped);
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 24,
      }}
    >
      <div style={{ display: 'flex', gap: 12 }}>
        <Icon size={20} style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 2 }} />
        <div>
          <div style={{ color: 'var(--text)', fontWeight: 600, fontSize: 15 }}>{title}</div>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: '6px 0 0', maxWidth: 560 }}>
            {description}
          </p>
        </div>
      </div>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={draft}
          disabled={busy}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') {
              setDraft(String(value));
              e.currentTarget.blur();
            }
          }}
          style={{
            width: 72,
            padding: '8px 10px',
            background: 'var(--bg)',
            color: 'var(--text)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-sm)',
            fontSize: 14,
            textAlign: 'right',
            opacity: busy ? 0.6 : 1,
          }}
        />
        {suffix ? <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{suffix}</span> : null}
      </div>
    </div>
  );
}

export default function SettingsView({ user }) {
  const isAdmin = user?.role === 'ADMIN';
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSettings(await apiSettings.get());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (key, value) => {
    setBusyKey(key);
    setError(null);
    // Optimiste : on reflète immédiatement, on confirme avec la réponse serveur.
    setSettings((s) => ({ ...s, [key]: value }));
    try {
      const updated = await apiSettings.update({ [key]: value });
      setSettings(updated);
    } catch (e) {
      setError(e.message);
      await load(); // resync en cas d'échec.
    } finally {
      setBusyKey(null);
    }
  };

  if (!isAdmin) {
    return (
      <div style={{ padding: 32, color: 'var(--text-muted)' }}>
        Accès réservé aux administrateurs.
      </div>
    );
  }
  if (loading) {
    return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Chargement…</div>;
  }

  return (
    <div style={{ padding: '24px 32px', maxWidth: 880, margin: '0 auto' }}>
      <h1
        style={{
          margin: 0,
          fontSize: 22,
          color: 'var(--text)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <SlidersHorizontal size={20} /> Paramètres
      </h1>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 8 }}>
        Activation / désactivation des fonctionnalités globales de l'application.
      </p>

      {error ? (
        <div
          style={{
            marginTop: 16,
            padding: '10px 14px',
            background: 'var(--glass)',
            border: '1px solid var(--danger)',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--danger)',
            fontSize: 13,
          }}
        >
          {error}
        </div>
      ) : null}

      <div style={{ ...cardStyle, marginTop: 24 }}>
        <ToggleRow
          Icon={Plane}
          title="Dépose de congés en self-service"
          description="Permet aux collaborateurs de créer et modifier leurs propres demandes de congés depuis l'onglet « Mes congés ». Désactivez ce paramètre si les congés validés sont poussés dans Synolog par une application tierce via l'API publique : l'onglet « Mes congés » devient alors une simple consultation et toute tentative de dépose côté collaborateur est refusée. Les administrateurs et l'API publique M2M ne sont jamais bloqués."
          enabled={Boolean(settings?.selfTimeOffRequestEnabled)}
          busy={busyKey === 'selfTimeOffRequestEnabled'}
          onToggle={(v) => toggle('selfTimeOffRequestEnabled', v)}
        />
      </div>

      <h2
        style={{
          margin: '32px 0 0',
          fontSize: 16,
          color: 'var(--text)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <UserCheck size={18} /> Authentification
      </h2>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 6 }}>
        Gestion de l'activation des nouveaux comptes. Les comptes en attente se valident
        depuis l'onglet « Utilisateurs ».
      </p>

      <div style={{ ...cardStyle, marginTop: 16 }}>
        <ToggleRow
          Icon={UserCheck}
          title="Activation automatique des nouveaux comptes"
          description="Active immédiatement (sans validation manuelle) tout nouveau compte créé à la première connexion. L'accès au login étant déjà restreint aux domaines email autorisés, ce paramètre revient à faire confiance à l'ensemble de ces domaines. Désactivé, chaque nouveau compte reste « en attente » jusqu'à validation par un administrateur dans l'onglet « Utilisateurs »."
          enabled={Boolean(settings?.autoActivateAllowedDomains)}
          busy={busyKey === 'autoActivateAllowedDomains'}
          onToggle={(v) => toggle('autoActivateAllowedDomains', v)}
        />
        <ToggleRow
          Icon={GitBranch}
          title="Promotion automatique des managers"
          description="Promeut automatiquement un collaborateur au rôle MANAGER lors de sa connexion s'il encadre au moins un subordonné direct actif (lien hiérarchique importé ou saisi). Montée seule : ce paramètre ne rétrograde jamais un compte, ne touche pas les administrateurs et n'écrase pas une promotion décidée manuellement. Désactivé, le rôle MANAGER doit être attribué à la main dans l'onglet « Utilisateurs »."
          enabled={Boolean(settings?.autoPromoteManagers)}
          busy={busyKey === 'autoPromoteManagers'}
          onToggle={(v) => toggle('autoPromoteManagers', v)}
        />
      </div>

      <h2
        style={{
          margin: '32px 0 0',
          fontSize: 16,
          color: 'var(--text)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <CalendarDays size={18} /> Planification (Schedule)
      </h2>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 6 }}>
        Ouverture de l'édition du calendrier de planification aux collaborateurs.
      </p>

      <div style={{ ...cardStyle, marginTop: 16 }}>
        <ToggleRow
          Icon={CalendarDays}
          title="Planification en self-service"
          description="Permet aux collaborateurs de créer et modifier des allocations sur le Schedule. Un USER ne peut planifier que sur sa propre fiche ; un MANAGER sur la sienne et celle de toute son équipe (descendance hiérarchique). Le changement de personne d'une allocation reste réservé aux administrateurs. Désactivé, la planification reste réservée aux administrateurs (les autres comptes consultent en lecture seule)."
          enabled={Boolean(settings?.selfScheduleEnabled)}
          busy={busyKey === 'selfScheduleEnabled'}
          onToggle={(v) => toggle('selfScheduleEnabled', v)}
        />
      </div>

      <h2
        style={{
          margin: '32px 0 0',
          fontSize: 16,
          color: 'var(--text)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <Clock size={18} /> Saisie des temps (Logtime)
      </h2>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 6 }}>
        Règles de saisie appliquées aux temps loggés par les collaborateurs.
      </p>

      <div style={{ ...cardStyle, marginTop: 16 }}>
        <NumberRow
          Icon={Clock}
          title="Durée minimale d'une saisie"
          description="Durée minimale (en heures) qu'un collaborateur peut loguer sur une affectation. Empêche les saisies trop courtes (ex. 0,25 h = 15 min). Toute saisie strictement positive en deçà de ce seuil est refusée (une suppression — remise à 0 — reste possible). Mettez 0 pour désactiver toute contrainte."
          value={settings?.logtimeMinHours ?? 1}
          min={0}
          max={24}
          step={0.25}
          integer={false}
          suffix="h"
          busy={busyKey === 'logtimeMinHours'}
          onCommit={(v) => toggle('logtimeMinHours', v)}
        />
      </div>

      <h2
        style={{
          margin: '32px 0 0',
          fontSize: 16,
          color: 'var(--text)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <LayoutDashboard size={18} /> Tableau de bord (Accueil)
      </h2>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 6 }}>
        Réglages des indicateurs et des seuils d'alerte affichés sur la page d'accueil.
      </p>

      <div style={{ ...cardStyle, marginTop: 16, display: 'flex', flexDirection: 'column', gap: 24 }}>
        <NumberRow
          Icon={SlidersHorizontal}
          title="Horizon de planification"
          description="Nombre de semaines à venir prises en compte pour évaluer l'état du scheduling sur le tableau de bord."
          value={settings?.dashboardSchedulingWeeks ?? 4}
          min={1}
          max={52}
          suffix="sem."
          busy={busyKey === 'dashboardSchedulingWeeks'}
          onCommit={(v) => toggle('dashboardSchedulingWeeks', v)}
        />
        <NumberRow
          Icon={SlidersHorizontal}
          title="Seuil d'alerte — planification"
          description="Taux de planification (heures allouées / capacité théorique) sous lequel une alerte est affichée sur la fenêtre de scheduling."
          value={settings?.dashboardSchedulingThresholdPct ?? 80}
          min={0}
          max={100}
          suffix="%"
          busy={busyKey === 'dashboardSchedulingThresholdPct'}
          onCommit={(v) => toggle('dashboardSchedulingThresholdPct', v)}
        />
        <NumberRow
          Icon={SlidersHorizontal}
          title="Seuil d'alerte — saisie des temps"
          description="Taux de saisie logtime (heures saisies / capacité attendue) sous lequel une alerte est affichée pour la semaine précédente et la semaine en cours."
          value={settings?.dashboardLogtimeThresholdPct ?? 90}
          min={0}
          max={100}
          suffix="%"
          busy={busyKey === 'dashboardLogtimeThresholdPct'}
          onCommit={(v) => toggle('dashboardLogtimeThresholdPct', v)}
        />
      </div>

      <h2
        style={{
          margin: '32px 0 0',
          fontSize: 16,
          color: 'var(--text)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <Wallet size={18} /> Budget
      </h2>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 6 }}>
        Seuil de consommation budget (TJM × heures saisies) déclenchant une alerte sur les
        affaires dont l'alerte budget est activée.
      </p>

      <div style={{ ...cardStyle, marginTop: 16, display: 'flex', flexDirection: 'column', gap: 24 }}>
        <NumberRow
          Icon={Wallet}
          title="Seuil d'alerte budget"
          description="Pourcentage de consommation du budget total à partir duquel une affaire remonte en alerte (badge + tableau de bord)."
          value={settings?.budgetAlertThresholdPct ?? 90}
          min={0}
          max={100}
          suffix="%"
          busy={busyKey === 'budgetAlertThresholdPct'}
          onCommit={(v) => toggle('budgetAlertThresholdPct', v)}
        />
        <NumberRow
          Icon={Wallet}
          title="Seuil d'alerte budget collaborateur"
          description="Pourcentage de consommation à partir duquel une affaire remonte en alerte dans la vue succincte « Budget de mes affaires » du tableau de bord (indicateur seul, sans montant)."
          value={settings?.budgetCollaboratorAlertThresholdPct ?? 80}
          min={0}
          max={100}
          suffix="%"
          busy={busyKey === 'budgetCollaboratorAlertThresholdPct'}
          onCommit={(v) => toggle('budgetCollaboratorAlertThresholdPct', v)}
        />
        <NumberRow
          Icon={Wallet}
          title="Marge d'alerte budget (jours restants)"
          description="Seuil par défaut : une affaire remonte en alerte quand il reste ≤ ce nombre de jours de budget. Surchargeable par affaire."
          value={settings?.budgetAlertRemainingDaysDefault ?? 5}
          min={0}
          max={3650}
          suffix="j"
          busy={busyKey === 'budgetAlertRemainingDaysDefault'}
          onCommit={(v) => toggle('budgetAlertRemainingDaysDefault', v)}
        />
        <NumberRow
          Icon={Wallet}
          title="Horizon de projection budget (jours)"
          description="Fenêtre de projection du planifié futur : une affaire remonte en alerte si la date de bascule dans le rouge tombe dans cet horizon."
          value={settings?.budgetAlertProjectionHorizonDays ?? 30}
          min={0}
          max={3650}
          suffix="j"
          busy={busyKey === 'budgetAlertProjectionHorizonDays'}
          onCommit={(v) => toggle('budgetAlertProjectionHorizonDays', v)}
        />
      </div>

      <h2
        style={{
          margin: '32px 0 0',
          fontSize: 16,
          color: 'var(--text)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <FolderKey size={18} /> Affaires internes
      </h2>
      <div style={{ ...cardStyle, marginTop: 16 }}>
        <InternalClientRow
          value={settings?.internalAffaireClientId ?? null}
          busy={busyKey === 'internalAffaireClientId'}
          onCommit={(v) => toggle('internalAffaireClientId', v)}
        />
      </div>
    </div>
  );
}
